/**
 * Every conversation in a project.
 *
 * The user asked for "a list of recent comments at some place" — this is that
 * place. Each thread names the file it is about and opens it, because a comment
 * about a drawing is only useful next to the drawing.
 *
 * It is the same conversation component as the side panel, so the two can never
 * drift apart: the list scrolls, the composer is docked at the bottom, and a
 * colleague's comment arriving does not move the page under someone who has
 * scrolled up to read something.
 */
import { useMemo } from 'react'
import { MessagesSquare, AtSign } from 'lucide-react'
import { buildThreads, notifiableFor, threadSize, type ProjectId } from '@lattice/core'
import { useWorkspace } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { CommentView, Discussion } from '@/components/Comments'
import { EmptyState, Badge, Button } from '@/components/ui/primitives'
import { pluralise } from '@/lib/format'

export function CommentsView({ projectId }: { projectId: ProjectId }) {
  const comments = useWorkspace(s => s.comments)
  const me = useWorkspace(s => s.user)
  const { open, go } = useUi()

  const threads = useMemo(
    () => buildThreads(comments.filter(c => c.projectId === projectId)),
    [comments, projectId],
  )

  const forMe = useMemo(
    () => (me ? notifiableFor(threads, me.id) : []),
    [threads, me],
  )

  const total = threads.reduce((n, t) => n + threadSize(t), 0)

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <Discussion
          projectId={projectId}
          threads={threads}
          showPath
          placeholder="Comment on this project…"
          header={
            <header className="flex h-9 shrink-0 items-center gap-2 border-b border-[var(--border)] bg-app px-3">
              <MessagesSquare className="h-3.5 w-3.5 text-[var(--fg-subtle)]" strokeWidth={1.75} />
              <h2 className="text-sm font-medium">Discussion</h2>
              <span className="tabular text-xs text-[var(--fg-subtle)]">
                {pluralise(threads.length, 'thread')} · {pluralise(total, 'comment')}
              </span>
            </header>
          }
          empty={
            <EmptyState
              icon={MessagesSquare}
              title="No comments in this project"
              hint="Open a file and start a thread, or comment on the project as a whole below."
            />
          }
        />
      </div>

      <aside className="hidden w-72 shrink-0 flex-col border-l border-[var(--border)] xl:flex">
        <header className="flex h-9 shrink-0 items-center gap-2 border-b border-[var(--border)] px-3">
          <AtSign className="h-3.5 w-3.5 text-[var(--fg-subtle)]" strokeWidth={1.75} />
          <h2 className="text-sm font-medium">For you</h2>
          {forMe.length > 0 && <Badge tone="accent">{forMe.length}</Badge>}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {forMe.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-[var(--fg-subtle)]">
              Nothing needs your attention.
            </p>
          ) : (
            <div className="divide-y divide-[var(--border)]">
              {forMe.map(comment => (
                <div key={comment.id} className="px-3 py-2">
                  <CommentView comment={comment} showPath />
                  {comment.path && (
                    <Button
                      size="sm" variant="ghost" className="mt-1"
                      onClick={() => { go({ projectId, view: 'files' }); open(comment.path as string) }}
                    >
                      Open file
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </div>
  )
}
